import * as React from "react";
import { useState, useEffect, useCallback } from "react";
// Material UI Components
import Box from "@mui/material/Box";
import TextField from "@mui/material/TextField";
import Paper from "@mui/material/Paper";
import Button from "@mui/material/Button";
import IconButton from "@mui/material/IconButton";
import DeleteIcon from "@mui/icons-material/Delete";
import { Checkbox, Divider, Stack, Typography } from "@mui/material";
import { useIsMobile } from "../../hooks/useIsMobile";
import { useToast } from "../../context/ToastContext";
import API from "../../api/axiosConfig";

export default function InsuranceForm() {
  const showToast = useToast();

  // Initialize the mobile checker
  const isMobile = useIsMobile();

  // Loading variables
  const [userLoading, setUserLoading] = useState(true);
  const [entryLoading, setEntryLoading] = useState(true);

  // Constant values for user information
  const [userBirthday, setUserBirthday] = useState(null);
  // does user profile include birthday?
  const [isBirthdaySet, setIsBirthdaySet] = useState(false);
  const [userFirst, setUserFirst] = useState(null);
  const [userLast, setUserLast] = useState(null);

  // Flag for whether the Build New Plan button is pressed
  const [buildFlag, setBuildFlag] = useState(false);

  // Flag for whether calculate was pressed or not
  const [calcFlag, setCalcFlag] = useState(false);

  // Flag for whether update is in progress
  const [updateFlag, setUpdateFlag] = useState(false);

  // Initializes the rows variable to contain the first row.
  const [rows, setRows] = useState([
    {
      rowId: 1,
      fields: [
        {
          id: 1,
          label: "Your First Name",
          type: "text",
          value: userFirst || "",
          error: false,
          readOnly: true,
        },
        {
          id: 2,
          label: "Your Last Name",
          type: "text",
          value: userLast || "",
          error: false,
          readOnly: true,
        },
        {
          id: 3,
          label: "Date of Birth",
          type: "date",
          value: userBirthday || "",
          error: false,
          readOnly: true,
        },
      ],
    },
  ]);

  // Info variable fetched from backend.
  const [entryInfo, setEntryInfo] = useState(null);

  // Variables holding the calculated cost for each plan type.
  const [bronzeCost, setBronzeCost] = useState(null);
  const [silverCost, setSilverCost] = useState(null);
  const [platinumCost, setPlatinumCost] = useState(null);
  const [companyCost, setCompanyCost] = useState(null);

  // Constant variable for plan selection
  const [selectedPlan, setSelectedPlan] = useState(null);

  // Constant variables for dental and vision selections
  const [dental, setDental] = useState(false);
  const [vision, setVision] = useState(false);

  // Constant variable for spouse inclusion
  const [spouse, setSpouse] = useState(false);

  // Constant variables for dental and vision costs
  const [visionCost, setVisionCost] = useState(null);
  const [dentalCost, setDentalCost] = useState(null);
  

  const createDependentRequest = (rows) =>
    rows.slice(1).map((row) => {
      const firstName =
        row.fields.find((f) => f.label.includes("First Name"))?.value || "";
      const lastName =
        row.fields.find((f) => f.label.includes("Last Name"))?.value || "";
      const birthday =
        row.fields.find((f) => f.label.includes("Date of Birth"))?.value ||
        null;

      return {
        firstName: firstName,
        lastName: lastName,
        birthday: birthday,
      };
    });

  // This function creates a new row to be displayed on the form.
  const addRow = () => {
    const newRowId = rows.length + 1;
    setRows((prevRows) => [
      ...prevRows,
      {
        rowId: newRowId,
        fields: [
          {
            id: newRowId * 3 - 2,
            label: "First Name",
            type: "text",
            value: "",
            error: false,
            shrink: false,
            readOnly: false,
          },
          {
            id: newRowId * 3 - 1,
            label: "Last Name",
            type: "text",
            value: "",
            error: false,
            shrink: false,
            readOnly: false,
          },
          {
            id: newRowId * 3,
            label: "Date of Birth",
            type: "date",
            value: "",
            error: false,
            shrink: true,
            readOnly: false,
          },
        ],
      },
    ]);
  };

  // This function handles the removal of a row triggered by the delete button being pressed.
  const removeRow = (rowId) => {
    setRows((prevRows) => prevRows.filter((row) => row.rowId !== rowId));
  };

  const detectChanges = () => {
    // Boolean conversions
    const visBool = entryInfo.vision === "true";
    const denBool = entryInfo.dental === "true";

    // Check for changes in plan type
    if (entryInfo.planType !== selectedPlan) {
      return true;
    }
    // Check for changes in vision or dental status
    if (vision !== visBool) {
      return true;
    }
    if (dental !== denBool) {
      return true;
    }

    // Return false if no major changes sensed
    return false;
  };

  // This function handles any update in the input fields and maps them to the rows variable.
  const handleInputChange = (rowId, fieldId, value) => {
    setRows((prevRows) =>
      prevRows.map((row) =>
        row.rowId === rowId
          ? {
              ...row,
              fields: row.fields.map((field) =>
                field.id === fieldId
                  ? { ...field, value, error: false }
                  : field,
              ),
            }
          : row,
      ),
    );
  };

  // This function checks to make sure the form entry information is valid.
  const validateForm = () => {
    let isValid = true;
    const today = new Date();

    setRows((prevRows) =>
      prevRows.map((row) => ({
        ...row,
        fields: row.fields.map((field) => {
          if (!field.value) {
            isValid = false;
            return { ...field, error: true };
          }
          if (field.type === "date") {
            const enteredDate = new Date(field.value);
            if (isNaN(enteredDate.getTime()) || enteredDate > today) {
              isValid = false;
              return {
                ...field,
                error: true,
              };
            }
          }
          return field;
        }),
      })),
    );

    if (!isValid) {
      alert(
        "Please fill out all fields correctly. Ensure the birthdate is valid.",
      );
    }
    return isValid;
  };

  // Function used to handle the calculate button being pressed.
  const handleCalculate = async (event) => {
    event.preventDefault();

    if (validateForm()) {
      const parentBirthday = rows.map(
        (row) =>
          row.fields.find((field) => field.label.includes("Date of Birth"))
            ?.value,
      )[0];

      // create dependentRequest objects
      const dependentRequests = createDependentRequest(rows);

      // Add or update the insurance entry for the user..
      const calculateCosts = async () => {
        try {
          // if birthday isn't set in profile update profile with birthday
          if (!isBirthdaySet) {
            const formData = new FormData();
            formData.append("deleteResume", false);
            formData.append("birthday", parentBirthday);

            await API.post("/api/users/updateProfile", formData);
          }
          const entryResponse = await API.post("/api/insurance/calculate", {
            dependents: dependentRequests,
            spouse: spouse,
          });

          setBronzeCost(entryResponse.data.bronzeCost);
          setSilverCost(entryResponse.data.silverCost);
          setPlatinumCost(entryResponse.data.platinumCost);
          setCompanyCost(entryResponse.data.companyCost);
          setVisionCost(entryResponse.data.visionCost);
          setDentalCost(entryResponse.data.dentalCost);

          // SUCCESS: Now that we have data, update the UI state
          // Update calc and update flag
          setCalcFlag(true);
          setUpdateFlag(true);

          // Update the build flag
          setBuildFlag(false);

          // Defualts to no selection of plan type
          setSelectedPlan(null);
          setVision(false);
          setDental(false);
        } catch (err) {
          if (err.response?.status !== 401) {
            showToast(
              "Error calculating plan costs",
            "error");
            console.error("Error calculating plan costs:", err);
          }
          // Optional: If calculation fails,
          // ensure the calcFlag stays false so they don't see empty results
          setCalcFlag(false);
        }
      };

      // Makes sure the entry addition is completed before getting the entry information.
      await calculateCosts();
    }
  };

  // Selects the plan locally when a plan is clicked.
  const handleSelectPlan = (plan) => {
    if (updateFlag) {
      setSelectedPlan(plan);
    }
  };

  // Handles the opt out submission button.
  const handleOptOut = () => {
    const isConfirmed = window.confirm(
      "Are you sure you want to opt out of your insurance plan?",
    );

    if (isConfirmed) {
      // Delete the insurance entry for the user if it exists
      const removePlan = async () => {
        try {
          const response = await API.post("/api/insurance/optout");

          setEntryInfo(response.data);
          alert("Opted out of insurance!");

          setSelectedPlan(null);
          setBronzeCost(null);
          setSilverCost(null);
          setPlatinumCost(null);
          setDental(false);
          setVision(false);
        } catch (err) {
          alert(
            "Error opting out of insurance! Check console for more details.",
          );
        }
      };

      removePlan();
      setCalcFlag(false);
    }
  };

  // This function handles the clicking of the 'Enroll' button and adds/modifies a user's entry to the database.
  const handleEnroll = (event) => {
    if (!validateForm()) {
      return;
    } else if (selectedPlan === null) {
      alert("Please select a plan before proceeding!");
    }

    // create dependentRequest objects
    const dependentRequests = createDependentRequest(rows);

    // Add or update the insurance entry for the user..
    const addEntry = async () => {
      try {
        const response = await API.post("/api/insurance/add", {
          dependents: dependentRequests,
          planType: selectedPlan.toUpperCase(),
          vision: vision,
          dental: dental,
          spouse: spouse,
        });

        alert(
          "Successfully enrolled in the " + response.data.planType + " plan!",
        );
        // Sets the user info accordingly.
        setEntryInfo(response.data);
        return true;
      } catch (err) {
        alert("Error adding user entry! Check console for more details.");
      }
    };

    addEntry();
    setCalcFlag(false);
    setUpdateFlag(false);
  };

  const handleUpdate = (event) => {
    if (selectedPlan === null) {
      alert("ERROR: Please select a plan first!");
      return;
    }
    // Add or update the insurance entry for the user..
    const updateEntry = async () => {
      try {
        const response = await API.post("/api/insurance/update", {
          planType: selectedPlan.toUpperCase(),
          vision: vision,
          dental: dental,
        });

        alert(
          "Successfully enrolled in the " + response.data.planType + " plan!",
        );
        // Sets the user info accordingly.
        setEntryInfo(response.data);
        return true;
      } catch (err) {
        if (err.response?.status !== 401) {
          alert("Error updating user entry! Check console for more details.");
          console.error("Error updating user entry:", err);
        }
      }
    };

    updateEntry();
    setUpdateFlag(false);
  };

  const getUserInfo = useCallback(async () => {
    setUserLoading(true);
    let first = null;
    let last = null;
    let birthday = null;
    try {
      const infoResponse = await API.post("/api/users/info");

      birthday = infoResponse.data.birthday;
      setUserBirthday(birthday);

      if ( birthday === null || birthday === "") {
        setIsBirthdaySet(false);
      } else {
        setIsBirthdaySet(true);
      }

      const fullName = infoResponse.data.name;
      if (fullName) {
        const [fName, lName] = fullName.split(" ");
        first = fName;
        last = lName;
        setUserFirst(first);
        setUserLast(last);
      }
    } catch (err) {
      if (err.response?.status !== 401) {
        console.error("Error gathering user birthday: ", err);
      }
    } finally {
      // Update the rows state with the fetched user information
      setRows([
        {
          rowId: 1,
          fields: [
            {
              id: 1,
              label: "Your First Name",
              type: "text",
              value: first || "",
              error: false,
              readOnly: true,
            },
            {
              id: 2,
              label: "Your Last Name",
              type: "text",
              value: last || "",
              error: false,
              readOnly: true,
            },
            {
              id: 3,
              label: "Date of Birth",
              type: "date",
              value: birthday || "",
              error: false,
              readOnly: true,
            },
          ],
        },
      ]);
      setUserLoading(false);
    }
  }, []);

  // Function to fetch entry information
  const getEntryInfo = useCallback(async () => {
    setEntryLoading(true);

    try {
      const response = await API.post("/api/insurance/get");

      // Sets the entry info to the previously fetched data.
      setEntryInfo(response.data);

      // Update the costs if there is not an existing plan.
      if (
        response.data.planType === "OPT_OUT" ||
        response.data.planType === "NONE"
      ) {
        setBronzeCost(null);
        setSilverCost(null);
        setPlatinumCost(null);
        setSelectedPlan(null);
        setDentalCost(null);
        setVisionCost(null);
        setDental(false);
        setVision(false);
        setSpouse(false);
      } else {
        // state variables to calculate amounts
        let finalBronze = response.data.bronze;
        let finalSilver = response.data.silver;
        let finalPlatinum = response.data.platinum;

        setSelectedPlan(response.data.planType);

        // Set the potential costs for vision and dental coverage
        setVisionCost(response.data.visionCost);
        setDentalCost(response.data.dentalCost);
        setVision(response.data.vision);
        setDental(response.data.dental);
        setSpouse(response.data.spouse);

        // calculate tier and company costs
        if (response.data.vision) {
          const vision = response.data.visionCost;
          finalBronze += vision;
          finalSilver += vision;
          finalPlatinum += vision;
        }
        if (response.data.dental) {
          const dental = response.data.dentalCost;
          finalBronze += dental;
          finalSilver += dental;
          finalPlatinum += dental;
        }
        console.log("Bronze: " + finalBronze + ", Silver: " + finalSilver + ", Plat: " + finalPlatinum);
        setBronzeCost(finalBronze);
        setSilverCost(finalSilver);
        setPlatinumCost(finalPlatinum);
        setCompanyCost(response.data.companyCost);
      }
    } catch (err) {
      showToast("Error loading user information", "error");
    }
    setEntryLoading(false);
  }, [showToast]);

  // Use useEffect to call getEntryInfo when the component mounts
  useEffect(() => {
    getEntryInfo();
    getUserInfo();
  }, [getEntryInfo, getUserInfo]);

  // Function used to toggle dental selection
  function toggleDental() {
    if (updateFlag) {
      if (dental) {
        setDental(false);
        // Adjust costs
        setBronzeCost(parseFloat(bronzeCost) - parseFloat(dentalCost));
        setSilverCost(parseFloat(silverCost) - parseFloat(dentalCost));
        setPlatinumCost(parseFloat(platinumCost) - parseFloat(dentalCost));
        setCompanyCost(parseFloat(companyCost) - parseFloat(dentalCost));
      } else {
        setDental(true);
        // Adjust costs
        setBronzeCost(parseFloat(bronzeCost) + parseFloat(dentalCost));
        setSilverCost(parseFloat(silverCost) + parseFloat(dentalCost));
        setPlatinumCost(parseFloat(platinumCost) + parseFloat(dentalCost));
        setCompanyCost(parseFloat(companyCost) + parseFloat(dentalCost));
      }
    }
  }
  // Function used to toggle vision selection
  function toggleVision() {
    if (updateFlag) {
      if (vision) {
        setVision(false);
        // Adjust costs
        setBronzeCost(parseFloat(bronzeCost) - parseFloat(visionCost));
        setSilverCost(parseFloat(silverCost) - parseFloat(visionCost));
        setPlatinumCost(parseFloat(platinumCost) - parseFloat(visionCost));
        setCompanyCost(parseFloat(companyCost) - parseFloat(visionCost));
      } else {
        setVision(true);
        // Adjust costs
        setBronzeCost(parseFloat(bronzeCost) + parseFloat(visionCost));
        setSilverCost(parseFloat(silverCost) + parseFloat(visionCost));
        setPlatinumCost(parseFloat(platinumCost) + parseFloat(visionCost));
        setCompanyCost(parseFloat(companyCost) + parseFloat(visionCost));
      }
    }
  }

  function handleCancel() {
    getEntryInfo();
    setUpdateFlag(false);
    setBuildFlag(false);
    setCalcFlag(false);
  }

  function handleSpouseClick() {
    setSpouse(!spouse);
  }

  const tierCosts = {
    BRONZE: bronzeCost,
    SILVER: silverCost,
    PLATINUM: platinumCost,
  };

  return (
    <Box>
      {!userLoading && !entryLoading ? (
        <Box display="flex" alignItems="center">
          <Paper
            sx={{
              py: "64px",
              margin: "16px auto",
              width: "90vw",
              maxWidth: "900px",
            }}
          >
            <Typography variant={isMobile ? "h6" : "h5"}>
              <b>Your Insurance Plan Details</b>
            </Typography>
            {isMobile || true ? (
              <Box>
                <Typography fontSize={12} marginTop={1}>
                  {entryInfo?.planType === "OPT_OUT"
                    ? "Currently OPTED OUT of insurance."
                    : entryInfo?.planType === "NONE"
                      ? "Please select an insurance plan or opt out."
                      : entryInfo?.dependentsAmount === 1
                        ? `Currently enrolled in the ${entryInfo?.planType} plan with ${entryInfo?.dependentsAmount} dependent.`
                        : entryInfo?.dependentsAmount === 0
                          ? `Currently enrolled in the ${entryInfo?.planType} plan with 0 dependents.`
                          : `Currently enrolled in the ${entryInfo?.planType} plan with ${entryInfo?.dependentsAmount} dependents.`}
                </Typography>

                {buildFlag ? (
                  <Box
                    component="form"
                    sx={{}}
                    noValidate
                    autoComplete="off"
                    onSubmit={handleCalculate}
                  >
                    <h3>Plan Builder</h3>
                    <Stack
                      spacing={2}
                      sx={{
                        flexGrow: 1,
                        alignItems: "center",
                        flexWrap: "wrap",
                      }}
                    >
                      {rows.map((row) => (
                        <Box key={row.rowId}>
                          {isMobile ? (
                            <Stack
                              direction="column"
                              spacing={2}
                              sx={{ width: "100%" }}
                            >
                              {/* Row 1: First Name and Last Name */}
                              <Stack direction="row" spacing={2}>
                                <TextField
                                  id={`field-${row.fields[0].id}`}
                                  label={row.fields[0].label}
                                  type={row.fields[0].type}
                                  value={row.fields[0].value}
                                  onChange={(e) =>
                                    handleInputChange(
                                      row.rowId,
                                      row.fields[0].id,
                                      e.target.value,
                                    )
                                  }
                                  error={row.fields[0].error}
                                  helperText={
                                    row.fields[0].error
                                      ? "This field is required"
                                      : ""
                                  }
                                  variant="outlined"
                                  required
                                  sx={{ flex: 1 }}
                                  disabled={row.rowId === 1 ? true : false}
                                />
                                <TextField
                                  id={`field-${row.fields[1].id}`}
                                  label={row.fields[1].label}
                                  type={row.fields[1].type}
                                  value={row.fields[1].value}
                                  onChange={(e) =>
                                    handleInputChange(
                                      row.rowId,
                                      row.fields[1].id,
                                      e.target.value,
                                    )
                                  }
                                  error={row.fields[1].error}
                                  helperText={
                                    row.fields[1].error
                                      ? "This field is required"
                                      : ""
                                  }
                                  variant="outlined"
                                  required
                                  sx={{ flex: 1, minHeight: "40px" }}
                                  disabled={row.rowId === 1 ? true : false}
                                />
                              </Stack>
                              {/* Row 2: Date of Birth and Delete Button */}
                              <Stack
                                direction="row"
                                spacing={2}
                                sx={{ alignItems: "center" }}
                              >
                                <TextField
                                  id={`field-${row.fields[2].id}`}
                                  label={row.fields[2].label}
                                  type={row.fields[2].type}
                                  value={row.fields[2].value}
                                  onChange={(e) =>
                                    handleInputChange(
                                      row.rowId,
                                      row.fields[2].id,
                                      e.target.value,
                                    )
                                  }
                                  error={row.fields[2].error}
                                  helperText={
                                    row.fields[2].error
                                      ? "Enter a valid date"
                                      : ""
                                  }
                                  variant="outlined"
                                  required
                                  sx={{ flex: 1, height: "56px" }}
                                  slotProps={{
                                    inputLabel: { shrink: true },
                                    input: {
                                      sx: {
                                        height: "56px",
                                        padding: "12px 12px",
                                      },
                                    },
                                  }}
                                  disabled={
                                    row.rowId === 1 &&
                                    userBirthday !== null &&
                                    userBirthday !== "" && // <--- Changed to Logical AND (&&)
                                    userBirthday !== undefined
                                      ? true
                                      : false
                                  }
                                />
                                {row.rowId > 1 && (
                                  <IconButton
                                    aria-label="delete"
                                    onClick={() => removeRow(row.rowId)}
                                    sx={(theme) => ({
                                      color: theme.palette.paper.verylight,
                                      width: "33%",
                                    })}
                                  >
                                    <DeleteIcon />
                                  </IconButton>
                                )}
                              </Stack>
                              {row.rowId === 1 && rows.length > 1 && (
                                <Stack
                                  direction="row"
                                  justifyContent={"center"}
                                  alignItems={"center"}
                                  spacing={2}
                                >
                                  <Typography maxWidth={"50%"}>
                                    Are you including your spouse in coverage?
                                  </Typography>
                                  <Checkbox
                                    checked={spouse}
                                    onChange={() => handleSpouseClick()}
                                    sx={(theme) => ({
                                      "&.Mui-checked": {
                                        color: theme.palette.paper.verylight,
                                      },
                                    })}
                                  />
                                  <Typography
                                    sx={(theme) => ({
                                      color: theme.palette.paper.verylight,
                                    })}
                                  >
                                    {spouse ? <b>YES</b> : <b>NO</b>}
                                  </Typography>
                                </Stack>
                              )}
                            </Stack>
                          ) : (
                            <Stack
                              direction="column"
                              spacing={2}
                              sx={{ width: "100%" }}
                            >
                              {/* Row 1: First Name, Last Name, Birthdate, and delete */}
                              <Stack direction="row" spacing={2}>
                                <TextField
                                  id={`field-${row.fields[0].id}`}
                                  label={row.fields[0].label}
                                  type={row.fields[0].type}
                                  value={row.fields[0].value}
                                  onChange={(e) =>
                                    handleInputChange(
                                      row.rowId,
                                      row.fields[0].id,
                                      e.target.value,
                                    )
                                  }
                                  error={row.fields[0].error}
                                  helperText={
                                    row.fields[0].error
                                      ? "This field is required"
                                      : ""
                                  }
                                  variant="outlined"
                                  required
                                  sx={{ flex: 1, width: "33%" }}
                                  disabled={row.rowId === 1 ? true : false}
                                />
                                <TextField
                                  id={`field-${row.fields[1].id}`}
                                  label={row.fields[1].label}
                                  type={row.fields[1].type}
                                  value={row.fields[1].value}
                                  onChange={(e) =>
                                    handleInputChange(
                                      row.rowId,
                                      row.fields[1].id,
                                      e.target.value,
                                    )
                                  }
                                  error={row.fields[1].error}
                                  helperText={
                                    row.fields[1].error
                                      ? "This field is required"
                                      : ""
                                  }
                                  variant="outlined"
                                  required
                                  sx={{ flex: 1, minHeight: "40px" }}
                                  disabled={row.rowId === 1 ? true : false}
                                />
                                <TextField
                                  id={`field-${row.fields[2].id}`}
                                  label={row.fields[2].label}
                                  type={row.fields[2].type}
                                  value={row.fields[2].value}
                                  onChange={(e) =>
                                    handleInputChange(
                                      row.rowId,
                                      row.fields[2].id,
                                      e.target.value,
                                    )
                                  }
                                  error={row.fields[2].error}
                                  helperText={
                                    row.fields[2].error
                                      ? "Enter a valid date"
                                      : ""
                                  }
                                  variant="outlined"
                                  required
                                  sx={{ flex: 1, height: "56px" }}
                                  slotProps={{
                                    inputLabel: { shrink: true },
                                    input: {
                                      sx: {
                                        height: "56px",
                                        padding: "12px 12px",
                                      },
                                    },
                                  }}
                                  disabled={
                                    row.rowId === 1 &&
                                    userBirthday !== null &&
                                    userBirthday !== "" && // <--- Changed to Logical AND (&&)
                                    userBirthday !== undefined
                                      ? true
                                      : false
                                  }
                                />
                                {row.rowId > 1 && (
                                  <IconButton
                                    aria-label="delete"
                                    onClick={() => removeRow(row.rowId)}
                                    sx={(theme) => ({
                                      color: theme.palette.paper.verylight,
                                      width: "50px",
                                    })}
                                  >
                                    <DeleteIcon />
                                  </IconButton>
                                )}
                              </Stack>
                              {row.rowId === 1 && rows.length > 1 && (
                                <Stack
                                  direction="row"
                                  justifyContent={"center"}
                                  alignItems={"center"}
                                  spacing={2}
                                >
                                  <Typography>
                                    Are you including your spouse in coverage?
                                  </Typography>
                                  <Checkbox
                                    checked={spouse}
                                    onChange={() => handleSpouseClick()}
                                    sx={(theme) => ({
                                      "&.Mui-checked": {
                                        color: theme.palette.paper.verylight,
                                      },
                                    })}
                                  />
                                  <Typography
                                    sx={(theme) => ({
                                      color: theme.palette.paper.verylight,
                                    })}
                                  >
                                    {spouse ? <b>YES</b> : <b>NO</b>}
                                  </Typography>
                                </Stack>
                              )}
                            </Stack>
                          )}

                          <Divider
                            sx={(theme) => ({
                              border: `1px solid ${theme.palette.paper.light}`,
                              marginTop: 4,
                              marginBottom: 2,
                            })}
                          />
                        </Box>
                      ))}
                    </Stack>

                    <Stack direction="column" spacing={2}>
                      <Button
                        variant="outlined"
                        color="primary"
                        sx={{ width: "100%" }}
                        onClick={addRow}
                      >
                        Add Dependent
                      </Button>

                      <Stack direction="row" spacing={2}>
                        <Button
                          variant="contained"
                          sx={{ width: "50%" }}
                          type="submit"
                        >
                          Calculate
                        </Button>

                        <Button
                          variant="outlined"
                          color="primary"
                          sx={{ width: "50%" }}
                          onClick={() => handleCancel()}
                        >
                          Cancel
                        </Button>
                      </Stack>
                    </Stack>
                  </Box>
                ) : null}

                {(selectedPlan != null || calcFlag) && !buildFlag ? (
                  <Stack direction="column" spacing={2} margin="16px auto">
                    <Stack direction="row" spacing={2} justifyContent="center">
                      <Box
                        onClick={() => toggleVision()}
                        sx={(theme) => ({
                          height: "96px",
                          width: "50%",
                          outline: 1,
                          outlineColor: updateFlag
                            ? theme.palette.paper.light
                            : "transparent",
                          borderRadius: 3,
                          backgroundColor: vision
                            ? theme.palette.paper.light
                            : "transparent",
                          transition: "background-color 0.3s ease-in-out",
                          padding: 2,
                          "&:hover": {
                            backgroundColor:
                              !vision && updateFlag
                                ? theme.palette.paper.default
                                : null,
                          },
                        })}
                        alignContent={"center"}
                        justifyContent={"center"}
                      >
                        <Typography
                          sx={(theme) => ({
                            color: vision
                              ? theme.palette.paper.default
                              : theme.palette.paper.verylight,
                            transition: "color 0.3s ease-in-out",
                          })}
                        >
                          <b>VISION</b>
                        </Typography>
                        <Typography fontSize={12}>
                          Added Cost: ${parseFloat(visionCost).toFixed(2)}
                        </Typography>
                        <Typography fontSize={12}>
                          <i>Covered by S3K!</i>
                        </Typography>
                      </Box>
                      <Box
                        onClick={() => toggleDental()}
                        sx={(theme) => ({
                          height: "96px",
                          width: "50%",
                          outline: 1,
                          outlineColor: updateFlag
                            ? theme.palette.paper.light
                            : "transparent",
                          borderRadius: 3,
                          backgroundColor: dental
                            ? theme.palette.paper.light
                            : "transparent",
                          transition: "background-color 0.3s ease-in-out",
                          padding: 2,
                          "&:hover": {
                            backgroundColor:
                              !dental && updateFlag
                                ? theme.palette.paper.default
                                : null,
                          },
                        })}
                        alignContent={"center"}
                        justifyContent={"center"}
                      >
                        <Typography
                          sx={(theme) => ({
                            color: dental
                              ? theme.palette.paper.default
                              : theme.palette.paper.verylight,
                            transition: "color 0.3s ease-in-out",
                          })}
                        >
                          <b>DENTAL</b>
                        </Typography>
                        <Typography fontSize={12}>
                          Added Cost: ${parseFloat(dentalCost).toFixed(2)}
                        </Typography>
                        <Typography fontSize={12}>
                          <i>Covered by S3K!</i>
                        </Typography>
                      </Box>
                    </Stack>
                    {["BRONZE", "SILVER", "PLATINUM"].map((tier, index) => (
                      <Box
                        key={tier}
                        sx={(theme) => ({
                          height: "96px",
                          outline: 1,
                          outlineColor: updateFlag
                            ? theme.palette.paper.light
                            : "transparent",
                          borderRadius: 3,
                          backgroundColor:
                            selectedPlan === tier
                              ? theme.palette.paper.light
                              : "transparent",
                          transition:
                            "background-color 0.3s ease-in-out, outline-color 0.3s ease-in-out",
                          padding: 2,
                          "&:hover": {
                            backgroundColor:
                              selectedPlan !== tier && updateFlag
                                ? theme.palette.paper.default
                                : null,
                          },
                        })}
                        onClick={() => handleSelectPlan(tier)}
                        alignContent={"center"}
                      >
                        <Stack
                          direction="row"
                          spacing={2}
                          margin="8px auto"
                          justifyContent={"center"}
                        >
                          <Box
                            sx={{ width: "25%", minHeight: "100%" }}
                            alignContent={"center"}
                          >
                            <Typography
                              sx={(theme) => ({
                                color:
                                  selectedPlan === tier
                                    ? theme.palette.paper.default
                                    : theme.palette.paper.verylight,
                                transition: "color 0.3s ease-in-out",
                              })}
                            >
                              <b>{tier.toUpperCase()}</b>
                            </Typography>
                          </Box>
                          <Box
                            sx={{ width: "25", minHeight: "100%" }}
                            alignContent={"center"}
                          >
                            <Typography fontSize={12} sx={{ color: "green" }}>
                              You Pay:
                            </Typography>
                            <Typography sx={{ color: "green" }}>
                              $
                              {(
                                tierCosts[tier] - parseFloat(companyCost)
                              ).toFixed(2)}
                            </Typography>
                          </Box>
                          <Box
                            sx={{ width: "25%", minHeight: "100%" }}
                            alignContent={"center"}
                          >
                            <Typography fontSize={12} sx={{ color: "red" }}>
                              Company Pays:
                            </Typography>
                            <Typography sx={{ color: "red" }}>
                              ${parseFloat(companyCost).toFixed(2)}
                            </Typography>
                          </Box>
                          <Box
                            sx={{ width: "25%", minHeight: "100%" }}
                            alignContent={"center"}
                          >
                            <Typography fontSize={12}>
                              Total Monthly Cost:
                            </Typography>
                            <Typography>
                              ${parseFloat(tierCosts[tier]).toFixed(2)}
                            </Typography>
                          </Box>
                        </Stack>
                      </Box>
                    ))}
                    <Stack
                      direction="row"
                      justifyContent="center"
                      alignContent="center"
                      spacing={2}
                    >
                      <Button
                        variant={updateFlag ? "contained" : "outlined"}
                        sx={(theme) => ({
                          height: "96px",
                          width: "50%",
                        })}
                        onClick={
                          selectedPlan !== null && calcFlag
                            ? () => handleEnroll()
                            : updateFlag &&
                                detectChanges() &&
                                selectedPlan !== null
                              ? () => handleUpdate()
                              : updateFlag
                                ? () => handleCancel()
                                : () => setUpdateFlag(true)
                        }
                      >
                        {selectedPlan !== null && calcFlag
                          ? "Enroll"
                          : updateFlag &&
                              detectChanges() &&
                              selectedPlan !== null
                            ? "Update"
                            : updateFlag
                              ? "Cancel"
                              : "Make Changes"}
                      </Button>
                      <Button
                        variant="outlined"
                        sx={(theme) => ({
                          height: "96px",
                          width: "50%",
                          borderColor: theme.palette.paper.light,
                          backgroundColor: "transparent",
                        })}
                        disabled={
                          entryInfo.planType === "OPT_OUT" && !calcFlag
                            ? true
                            : null
                        }
                        onClick={
                          updateFlag
                            ? () => setBuildFlag(true)
                            : () => handleOptOut()
                        }
                      >
                        {updateFlag ? <>Build New Plan</> : <>Opt Out</>}
                      </Button>
                    </Stack>
                  </Stack>
                ) : !buildFlag ? (
                  <Stack
                    direction="row"
                    justifyContent={"center"}
                    marginTop={2}
                    spacing={2}
                  >
                    <Button
                      variant="contained"
                      onClick={() => {
                        setSpouse(false);
                        setBuildFlag(true);
                      }}
                      sx={{
                        width: "50%",
                      }}
                    >
                      Build New Plan
                    </Button>
                    {entryInfo.planType !== "OPT_OUT" ? (
                      <Button
                        variant="outlined"
                        onClick={() => handleOptOut()}
                        sx={{
                          width: "50%",
                        }}
                      >
                        Opt Out
                      </Button>
                    ) : null}
                  </Stack>
                ) : null}
              </Box>
            ) : null}
          </Paper>
        </Box>
      ) : (
        <i>Loading Insurance Plan Builder...</i>
      )}
    </Box>
  );
}
